import { numberDecimalSeparator } from "./numberUtils";
import { getNumberOfDigitsAfterDecimalPoint } from "./numericvalidation";
import { stripNonNumericLeaveSeparator } from "./stringutils";

// Shared locale-aware number input handling for web and React Native.
// Keeps parsing, formatting, and sanitization consistent across both apps.
const maxDigitsBeforeDecimal = 9;

export interface FormatLocalizedNumberOptions {
  decimalPoints: number;
  preserveEmptyValue?: boolean;
}

export interface ParseLocalizedNumberOptions {
  defaultWhenError?: number;
  minValue?: number;
  maxValue?: number;
}

export interface SanitizeLocalizedNumberInputOptions {
  decimalPoints: number;
  canBeNegative?: boolean;
  acceptAlternateDecimalSeparators?: boolean;
}

// Formats a numeric value for display using the locale's decimal separator.
export const formatLocalizedNumber = (
  value: number | string,
  locale: string,
  options: FormatLocalizedNumberOptions
): string => {
  if ((value == null || value === "") && options.preserveEmptyValue) {
    return "";
  }

  const number = Number(value);
  let result: string;

  if (options.decimalPoints > 2) {
    const actualDecimalPlaces = number.toString().split(".")[1]?.length ?? 0;
    result = number.toFixed(Math.max(actualDecimalPlaces, 2));
  } else {
    result = number.toFixed(options.decimalPoints);
  }

  const decimalSeparator = numberDecimalSeparator(locale);
  if (decimalSeparator !== ".") {
    result = result.replace(".", decimalSeparator);
  }

  return result;
}

// Parses localized input and applies its fallback and bounds.
export const parseLocalizedNumber = (
  input: string,
  locale: string,
  options: ParseLocalizedNumberOptions = {}
): number => {
  const decimalSeparator = numberDecimalSeparator(locale);
  let parsed = input.trim() === ""
    ? NaN
    : Number(input.replace(new RegExp(`[${decimalSeparator}]`, "g"), "."));

  if (Number.isNaN(parsed)) {
    parsed = options.defaultWhenError ?? 0;
  }
  if (options.minValue != null && parsed < options.minValue) {
    parsed = options.minValue;
  }
  if (options.maxValue != null && parsed > options.maxValue) {
    parsed = options.maxValue;
  }

  return parsed;
}

// Cleans and limits localized numeric text while allowing unfinished input during editing.
export const sanitizeLocalizedNumberInput = (
  input: string,
  locale: string,
  options: SanitizeLocalizedNumberInputOptions
): string => {
  const decimalSeparator = numberDecimalSeparator(locale);
  const normalizedInput = options.acceptAlternateDecimalSeparators && options.decimalPoints !== 0
    ? input.replace(/[.,]/g, decimalSeparator)
    : input;
  const containsNegativeSign = normalizedInput[0] === "-";
  let sanitizedInput = stripNonNumericLeaveSeparator(normalizedInput, locale, options.decimalPoints !== 0);

  if (sanitizedInput[0] === decimalSeparator) {
    sanitizedInput = "0" + sanitizedInput;
  }

  const splitNumbers = sanitizedInput.split(decimalSeparator);
  if (splitNumbers[0].length > maxDigitsBeforeDecimal) {
    const trimmedNumberBeforeDecimal = splitNumbers[0].substring(0, maxDigitsBeforeDecimal);
    sanitizedInput = sanitizedInput.replace(splitNumbers[0], trimmedNumberBeforeDecimal);
  }

  if (containsNegativeSign && options.canBeNegative) {
    sanitizedInput = "-" + sanitizedInput;
  }

  const digitsAfterDecimalPoint = getNumberOfDigitsAfterDecimalPoint(sanitizedInput, decimalSeparator);
  if (digitsAfterDecimalPoint > options.decimalPoints) {
    const numberOfDigitsToRemove = digitsAfterDecimalPoint - options.decimalPoints;
    sanitizedInput = sanitizedInput.substring(0, sanitizedInput.length - numberOfDigitsToRemove);
  }

  return sanitizedInput;
}
